// src/pages/admin/BulkCheckPreview.jsx
import { useEffect, useMemo, useState } from "react";
import { fmtLineAmount } from "@common";
import CheckPreview from "./CheckPreview";

/**
 * Bulk confirmation view: stacks the existing single-check CheckPreview
 * for every item in the batch. Each item is collapsible.
 *
 * Props:
 *   items    — [{ key, clinic, row, payload }, ...]
 *   onChange — optional (index, patch) => void. When provided, each item's
 *              preview is editable; the parent must update items[index].payload.
 *   results  — optional response payload after the batch has run:
 *              { summary: {total, succeeded, failed}, results: [{index, ok, error?, data?}] }
 */
export default function BulkCheckPreview({ items, onChange, results }) {
  const resultByIndex = useMemo(() => {
    const out = {};
    for (const r of results?.results ?? []) {
      if (r && typeof r.index === "number") out[r.index] = r;
    }
    return out;
  }, [results]);

  // Default: first item expanded, rest collapsed.
  const [expanded, setExpanded] = useState(() => new Set([0]));

  // When results arrive, auto-expand the failed rows so the user can see why.
  useEffect(() => {
    if (!results) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      for (const r of results.results ?? []) {
        if (r && !r.ok && typeof r.index === "number") next.add(r.index);
      }
      return next;
    });
  }, [results]);

  const total = useMemo(() => {
    let sum = 0;
    for (const it of items || []) {
      const amt = Number(it?.payload?.expense?.items?.[0]?.amount) || 0;
      sum += amt;
    }
    return sum;
  }, [items]);

  const toggle = (idx) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });

  const expandAll = () => setExpanded(new Set(items.map((_, i) => i)));
  const collapseAll = () => setExpanded(new Set());

  if (!items || items.length === 0) {
    return <div className="text-sm text-palette-gray-badge italic">No checks to create.</div>;
  }

  return (
    <div className="space-y-3">
      {/* Header summary */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        {results ? (
          <div className="flex items-center gap-2 text-sm">
            <span className="px-2 py-1 rounded-md bg-palette-gray-subtle text-palette-gray-text">
              Total: <strong>{results.summary?.total ?? items.length}</strong>
            </span>
            <span className="px-2 py-1 rounded-md bg-palette-green-muted text-palette-green-text">
              Succeeded: <strong>{results.summary?.succeeded ?? 0}</strong>
            </span>
            <span className="px-2 py-1 rounded-md bg-palette-red-muted text-palette-red-text">
              Failed: <strong>{results.summary?.failed ?? 0}</strong>
            </span>
          </div>
        ) : (
          <div className="text-sm text-palette-gray-heading">
            About to create <strong>{items.length}</strong> check{items.length === 1 ? "" : "s"}{" "}
            totaling <strong>${fmtLineAmount(total)}</strong>. Each check is sent independently — if
            one fails, the others will still proceed.
          </div>
        )}
        <div className="flex items-center gap-2 text-xs">
          <button
            type="button"
            onClick={expandAll}
            className="px-2 py-1 rounded border border-palette-gray-border bg-white text-palette-gray-heading hover:bg-palette-gray-soft"
          >
            Expand all
          </button>
          <button
            type="button"
            onClick={collapseAll}
            className="px-2 py-1 rounded border border-palette-gray-border bg-white text-palette-gray-heading hover:bg-palette-gray-soft"
          >
            Collapse all
          </button>
        </div>
      </div>

      {/* Stacked CheckPreview per item */}
      <div className="max-h-[65vh] overflow-y-auto space-y-2 pr-1">
        {items.map((it, idx) => {
          const result = resultByIndex[idx];
          const isOpen = expanded.has(idx);
          const amount = Number(it?.payload?.expense?.items?.[0]?.amount) || 0;
          const headerBg = result
            ? result.ok
              ? "bg-palette-green-soft hover:bg-palette-green-muted"
              : "bg-palette-red-soft hover:bg-palette-red-muted"
            : "bg-palette-gray-soft hover:bg-palette-gray-subtle";

          return (
            <div
              key={it.key ?? idx}
              className="rounded-md border border-palette-gray-divider bg-white"
            >
              <button
                type="button"
                onClick={() => toggle(idx)}
                className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-left ${headerBg}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs font-mono text-palette-gray-secondary w-6 shrink-0">
                    #{idx + 1}
                  </span>
                  <span className="text-sm font-semibold text-palette-gray-text truncate">
                    {it.clinic ?? "—"}
                  </span>
                  <span className="text-sm text-palette-gray-heading truncate">
                    {it?.row?.practitioner_name ?? "—"}
                  </span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-sm font-mono text-palette-gray-text">
                    ${fmtLineAmount(amount)}
                  </span>
                  {result &&
                    (result.ok ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-palette-green-muted text-palette-green-text text-xs font-medium">
                        Created
                      </span>
                    ) : (
                      <span
                        title={result.error || ""}
                        className="inline-flex items-center px-2 py-0.5 rounded-md bg-palette-red-muted text-palette-red-text text-xs font-medium max-w-[16rem] truncate"
                      >
                        {result.error || "Failed"}
                      </span>
                    ))}
                  <span className="text-palette-gray-secondary text-xs">{isOpen ? "▼" : "▶"}</span>
                </div>
              </button>

              {isOpen && (
                <div className="border-t border-palette-gray-divider p-3">
                  <CheckPreview
                    preview={it}
                    onChange={onChange && !results ? (patch) => onChange(idx, patch) : undefined}
                  />
                  {result && !result.ok && (
                    <div className="mt-3 rounded-md bg-palette-red-soft border border-palette-red-border p-2 text-xs text-palette-red-text whitespace-pre-wrap break-words">
                      <strong>Error:</strong> {result.error || "Unknown error"}
                      {result.error_code ? ` (${result.error_code})` : ""}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
